// Test comment for workflow deployment - frontend change
import React, { useState, useEffect } from 'react';
import { useAuth } from "react-oidc-context";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import UploadTab from './components/UploadTab';
import LibraryTab from './components/LibraryTab';
import MatchesTab from './components/MatchesTab';
import SponsorsTab from './components/SponsorsTab';
import PlayersTab from './components/PlayersTab';


import AnalyticsDashboard from './components/AnalyticsDashboard';
import { Toaster } from "./components/ui/toaster";
import { useUserRole } from './hooks/useUserRole';
import { setAuthContext } from './lib/api-client';

function MainContent() {
  const auth = useAuth();
  const userRole = useUserRole();
  
  // Set auth context for API client
  useEffect(() => {
    if (auth.isAuthenticated) {
      setAuthContext(auth);
    }
  }, [auth]);
  
  // Set default tab based on user role
  const getDefaultTab = () => {
    if (userRole.hasAnyRole(['admin', 'marketing', 'photographer'])) {
      return "upload";
    }
    return "library"; // Viewers and others default to library
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedSponsorId, setSelectedSponsorId] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const handleMatchSelect = (matchId) => {
    setSelectedMatchId(matchId);
    setSelectedSponsorId(null); // Clear sponsor filter when selecting match
    setSelectedPlayerId(null); // Clear player filter when selecting match
    setActiveTab("library");
  };

  const handleSponsorSelect = (sponsorId) => {
    setSelectedSponsorId(sponsorId);
    setSelectedMatchId(null); // Clear match filter when selecting sponsor
    setSelectedPlayerId(null); // Clear player filter when selecting sponsor
    setActiveTab("library");
  };

  const handlePlayerSelect = (playerId) => {
    setSelectedPlayerId(playerId);
    setSelectedMatchId(null); // Clear match filter when selecting player
    setSelectedSponsorId(null); // Clear sponsor filter when selecting player
    setActiveTab("library");
  };

  useEffect(() => {
    if (activeTab !== 'library') {
      setSelectedMatchId(null);
      setSelectedSponsorId(null);
      setSelectedPlayerId(null);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">SRFC Media Management</h1>
          {auth.isAuthenticated && auth.user && auth.user.profile && (
            <div className="flex items-center">
              <div className="mr-6 text-sm text-gray-600">
                <div>{auth.user.profile.email}</div>
                {userRole.groups.length > 0 && (
                  <div className="text-xs text-gray-500">
                    Role: {userRole.highestRole || userRole.groups.join(', ')}
                  </div>
                )}
              </div>
              <button onClick={() => auth.signoutRedirect()} className="text-sm text-gray-600 hover:text-gray-800 underline">
                Sign Out
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="container mx-auto py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            {/* Upload - Admin, Marketing, Photographer */}
            {userRole.hasAnyRole(['admin', 'marketing', 'photographer']) && (
              <TabsTrigger value="upload">Upload</TabsTrigger>
            )}
            
            {/* Library - All authenticated users */}
            <TabsTrigger value="library">Library</TabsTrigger>
            
            {/* Matches - Admin, Marketing, Photographer */}
            {userRole.hasAnyRole(['admin', 'marketing', 'photographer']) && (
              <TabsTrigger value="matches">Matches</TabsTrigger>
            )}
            
            {/* Sponsors - Admin, Marketing only */}
            {userRole.hasAnyRole(['admin', 'marketing']) && (
              <TabsTrigger value="sponsors">Sponsors</TabsTrigger>
            )}
            
            {/* Players - All authenticated users */}
            <TabsTrigger value="players">Players</TabsTrigger>
            
            {/* Analytics - Admin, Marketing only */}
            {userRole.hasAnyRole(['admin', 'marketing']) && (
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            )}
            

          </TabsList>
          {userRole.hasAnyRole(['admin', 'marketing', 'photographer']) && (
            <TabsContent value="upload"><UploadTab /></TabsContent>
          )}
          <TabsContent value="library"><LibraryTab selectedMatchId={selectedMatchId} selectedSponsorId={selectedSponsorId} selectedPlayerId={selectedPlayerId} setSelectedMatchId={setSelectedMatchId} setSelectedSponsorId={setSelectedSponsorId} setSelectedPlayerId={setSelectedPlayerId} /></TabsContent>
          {userRole.hasAnyRole(['admin', 'marketing', 'photographer']) && (
            <TabsContent value="matches"><MatchesTab setActiveTab={setActiveTab} setSelectedMatchId={handleMatchSelect} /></TabsContent>
          )}
          {userRole.hasAnyRole(['admin', 'marketing']) && (
            <TabsContent value="sponsors"><SponsorsTab setActiveTab={setActiveTab} setSelectedSponsorId={handleSponsorSelect} /></TabsContent>
          )}
          <TabsContent value="players"><PlayersTab setActiveTab={setActiveTab} setSelectedPlayerId={handlePlayerSelect} /></TabsContent>
          {userRole.hasAnyRole(['admin', 'marketing']) && (
            <TabsContent value="analytics"><AnalyticsDashboard /></TabsContent>
          )}

        </Tabs>
      </main>
      <Toaster />
    </div>
  );
}

function App() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>;
  }

  if (auth.isAuthenticated) {
    return <MainContent />;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <button onClick={() => auth.signinRedirect()} style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer' }}>
        Sign In
      </button>
    </div>
  );
}

export default App;